import { Icon, NavLink, NavLinks } from '@pypestream/design-system';
import { FC } from 'react';
import { useHref, useNavigate } from 'react-router';

import { type WorkflowStage } from '../stores/utils/workflow-stage';
import { setupNavItem } from './guided-setup/guided-setup-model';

import './stage-nav.scss';

interface StageNavProps {
  value: WorkflowStage;
  onChange: (next: WorkflowStage) => void;
  /**
   * Every stage's content lives on the one real `/workflows` route for now
   * (see `workflow-stage.ts`) — clicking a stage from anywhere else in the
   * solution (Versions, Channels, …) has to navigate there first, the same
   * way today's single "Agent Workflow" tab does.
   */
  workflowsPath: string;
  isOnWorkflowsPath: boolean;
  /**
   * Guided setup (`bobcat-guided-setup`): the furthest stage reached, which
   * numbers the tabs and locks the ones after it. Null out of setup.
   */
  setupReached?: number | null;
}

const STAGES: { value: WorkflowStage; label: string }[] = [
  { value: 'design', label: 'Design' },
  { value: 'build', label: 'Build' },
  { value: 'validate', label: 'Validate' },
  { value: 'deploy', label: 'Deploy' },
  { value: 'observe', label: 'Observe' },
];

/**
 * "Design / Build / Validate / Deploy / Observe" — Project Bobcat's 5-stage
 * nav, as the prototype draws it (`prostudio-build-flow-layouts.html`,
 * `<ps-nav-links>`): the header's own nav links, centred, the active stage
 * purple and underlined. Rendered into the header's `navigation` slot, which
 * a solution's pages don't otherwise use.
 */
export const StageNav: FC<StageNavProps> = ({
  value,
  onChange,
  workflowsPath,
  isOnWorkflowsPath,
  setupReached = null,
}) => {
  const navigate = useNavigate();
  const href = useHref(workflowsPath);

  return (
    <NavLinks
      slot="navigation"
      className="c-stage-nav"
      test-id="bobcat-stage-nav"
    >
      {STAGES.map((stage) => {
        // In guided setup: numbered 2–6 (Start is 1), a check once passed,
        // and shut until reached — the prototype's `renderSetupNav`.
        const setup =
          setupReached === null
            ? null
            : setupNavItem(stage.value, value, setupReached);
        return (
          <NavLink
            key={stage.value}
            test-id={`bobcat-stage-nav-${stage.value}`}
            active={value === stage.value}
            disabled={!!setup?.locked}
            title={setup?.title}
            // A real link, so it can be opened in a new tab; a plain click
            // switches the stage in place.
            href={href}
            onClick={(event: React.MouseEvent) => {
              if (setup?.locked) {
                event.preventDefault();
                return;
              }
              if (event.metaKey || event.ctrlKey || event.shiftKey) return;
              event.preventDefault();
              onChange(stage.value);
              if (!isOnWorkflowsPath) navigate(workflowsPath);
            }}
          >
            {setup && (
              <span
                className={`c-stage-nav__step${
                  setup.done ? ' c-stage-nav__step--done' : ''
                }`}
                aria-hidden
              >
                {setup.done ? <Icon name="check" size="xsmall" /> : setup.step}
              </span>
            )}
            {stage.label}
          </NavLink>
        );
      })}
    </NavLinks>
  );
};
